import React from "react";
import { atom, useAtom } from "jotai";

/**
 * Query queue system to throttle network requests during rapid user interactions.
 * Used for queries that need to respond to focus range changes (e.g. zooming)
 * but should not fire immediately upon every small change.
 */

export interface QueuedQuery {
  id: string; // Unique identifier for the query
  type: "dataDumpAggregate" | "productionSegments"; // Query type
  variables: Record<string, any>; // Query variables
  timestamp: number; // When it was added to queue
  extraData?: Record<string, any>; // Optional additional data needed for query execution
}

// Queue for pending queries
export const queryQueueAtom = atom<QueuedQuery[]>([]);

// Last processed timestamp to track which queries have already been processed
export const lastProcessedTimestampAtom = atom<number>(Date.now());

// Control flag to indicate whether queue processing is active
export const isProcessingQueueAtom = atom<boolean>(false);

/**
 * Adds a query to the queue, replacing any existing query of same type and ID
 */
export function useQueryQueue() {
  const [queue, setQueue] = useAtom(queryQueueAtom);
  const [isProcessing, setIsProcessing] = useAtom(isProcessingQueueAtom);
  const [lastProcessed, setLastProcessed] = useAtom(lastProcessedTimestampAtom);

  // Use a ref to track the last enqueued query to prevent duplicate enqueueing
  const lastEnqueuedRef = React.useRef<{
    id: string;
    type: string;
    hash: string;
  } | null>(null);

  // Memoize the enqueue function to maintain stable reference
  const enqueueQuery = React.useCallback(
    (query: Omit<QueuedQuery, "timestamp">) => {
      // Create a simple hash of the variables to compare
      const varsHash = JSON.stringify(query.variables);

      // Skip if this exact query was just enqueued (prevents infinite loops)
      if (
        lastEnqueuedRef.current &&
        lastEnqueuedRef.current.id === query.id &&
        lastEnqueuedRef.current.type === query.type &&
        lastEnqueuedRef.current.hash === varsHash
      ) {
        return;
      }

      // Update the last enqueued ref
      lastEnqueuedRef.current = {
        id: query.id,
        type: query.type,
        hash: varsHash,
      };

      setQueue(currentQueue => {
        // Remove any existing queries of the same type/id
        const filteredQueue = currentQueue.filter(
          q => !(q.type === query.type && q.id === query.id)
        );

        // Add the new query with current timestamp
        return [...filteredQueue, { ...query, timestamp: Date.now() }];
      });
    },
    [setQueue]
  );

  return {
    queue,
    enqueueQuery,
    isProcessing,
    setIsProcessing,
    lastProcessed,
    setLastProcessed,
  };
}

/**
 * Hook to process the query queue at a fixed interval
 */
export function useProcessQueryQueue(interval: number = 1000) {
  const {
    queue,
    // setIsProcessing not used, removing from destructuring
    lastProcessed,
    setLastProcessed,
  } = useQueryQueue();

  return {
    processQueue: () => {
      // Don't do anything if the queue is empty
      if (queue.length === 0) {
        return { queriesToProcess: [], newLastProcessed: lastProcessed };
      }

      // Get queries that were added since the last processing
      const queriesToProcess = queue.filter(q => q.timestamp > lastProcessed);

      // Group queries by type and ID to ensure we only process the latest variables
      const latestQueries = new Map<string, QueuedQuery>();

      queriesToProcess.forEach(query => {
        const key = `${query.type}:${query.id}`;

        // Only keep the query with the latest timestamp
        if (
          !latestQueries.has(key) ||
          (latestQueries.get(key)?.timestamp ?? 0) < query.timestamp
        ) {
          latestQueries.set(key, query);
        }
      });

      // Process the latest queries
      const newLastProcessed =
        queue.length > 0
          ? Math.max(...queue.map(q => q.timestamp), lastProcessed)
          : lastProcessed;
      setLastProcessed(newLastProcessed);

      return {
        queriesToProcess: Array.from(latestQueries.values()),
        newLastProcessed,
      };
    },
  };
}
